import { Box, Grid } from "@mui/system";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useComments } from "../../../../../Discussion/discussionHooks/useComments";
import { useFileUploads } from "../../../../../Discussion/discussionHooks/useFileUploads";
import {
  resetOfCommentsAndFiles,
  setDownloadFileTrackID,
  setModuleType,
  setMultiMetaSharedUid,
  setObjectSharedUid,
  setObjectType,
  setPostCommentTrackID,
  setPreviewFileTrackID,
  setReactionTrackID,
  setReferenceModule,
  setUploadFileTrackID,
} from "../../../../../../features/genericCommentsAndFilesSlice";
import ExpandableCommentBox from "../../../../../Discussion/ExpandableCommentBox";
import CommentList from "../../../../../Discussion/CommentList";
import Attachments from "../../../../../Discussion/Attachments";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

type Props = {
  type: "all" | "single";
  objectType: string;
  referenceModule: string;
  moduleType: any;
  objectSharedUid: string | string[];
  postCommentTrackID: any; // Track ID for posting comments
  reactionTrackID: any; // Track ID for reactions
  uploadFileTrackID: any; // Track ID for uploading files
  downloadFileTrackID: any; // Track ID for downloading files
  previewFileTrackID?: any; // Track ID for previewing files
};
const CommentsAndFiles = ({
  type,
  objectType,
  referenceModule,
  moduleType,
  objectSharedUid,
  postCommentTrackID,
  reactionTrackID,
  uploadFileTrackID,
  downloadFileTrackID,
  previewFileTrackID,
}: Props) => {
  const dispatch = useDispatch();
  const commentsLoader = useSelector(
    (state: any) => state.genericCommentsAndFiles.commentsLoader
  );
  const {
    hasFetched,
    commentList,
    addComment,
    handleUpdateComment,
    handleDeleteComment,
  } = useComments(); // custom hook for comments
  const {
    handleFileUpload,
    handleRemoveFile,
    handleFileDrop,
    isDragging,
    setIsDragging,
    files,
    fileUploadLoading,
    setFileUploadLoading,
  } = useFileUploads(); // custom hook for files

  // Set the object type, reference module and module type, tracking Id in the redux store(Avoid prop drilling)
  useEffect(() => {
    if (type === "single") {
      dispatch(setObjectSharedUid(objectSharedUid)); //shared uid specific to the object
      dispatch(setMultiMetaSharedUid([])); // clear multi shared uid
    } else if (type === "all") {
      dispatch(setMultiMetaSharedUid(objectSharedUid)); // Set the shared UIDs for multiple meta discussions
      dispatch(setObjectSharedUid("")); // clear single shared uid
    }
    dispatch(setObjectType(objectType)); // General Discussion object type
    dispatch(setReferenceModule(referenceModule)); // General Discussion reference module
    dispatch(setModuleType(moduleType)); // General Discussion module type
    dispatch(setPostCommentTrackID(postCommentTrackID)); // Track ID for posting comments
    dispatch(setReactionTrackID(reactionTrackID)); // Track ID for reactions
    dispatch(setUploadFileTrackID(uploadFileTrackID)); // Track ID for uploading files
    dispatch(setDownloadFileTrackID(downloadFileTrackID)); // Track ID for downloading files
    dispatch(setPreviewFileTrackID(previewFileTrackID)); // Track ID for previewing files

    return () => {
      dispatch(resetOfCommentsAndFiles());
    };

    // eslint-disable-next-line
  }, [
    objectType,
    referenceModule,
    moduleType,
    objectSharedUid,
    postCommentTrackID,
    reactionTrackID,
    uploadFileTrackID,
    downloadFileTrackID,
    previewFileTrackID,
  ]);
  return (
    <Box mt={2} mb={2}>
      <Grid container spacing={4}>
        {/* this is for dynamic tabs excluding All Forecast tab*/}
        {type === "single" && (
          <>
            <Grid size={7}>
              {/* expandable comment box */}
              <ExpandableCommentBox addComment={addComment} />
              {/* Comments */}
              <CommentList
                commentList={commentList}
                handleUpdateComment={handleUpdateComment}
                handleDeleteComment={handleDeleteComment}
              />
            </Grid>
            <Grid size={5}>
              <Attachments
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                handleFileDrop={handleFileDrop}
                handleFileUpload={handleFileUpload}
                fetchedFiles={files.length > 0 ? files : []}
                handleRemoveFile={handleRemoveFile}
                fileUploadLoading={fileUploadLoading}
                setFileUploadLoading={setFileUploadLoading}
              />
            </Grid>
          </>
        )}

        {/* this is for All Forecast Tab*/}
        {type === "all" && (
          <>
            <Grid size={12}>
              {/* Comments */}
              {hasFetched && !commentsLoader && commentList?.length === 0 && (
                <Typography sx={{ textAlign: "center", color: "#888", mt: 2 }}>
                  No comments yet.
                </Typography>
              )}

              <CommentList
                commentList={commentList}
                handleUpdateComment={handleUpdateComment}
                handleDeleteComment={handleDeleteComment}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default CommentsAndFiles;
